import React, { useState, useEffect, useContext, createContext } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Loader2, UserPlus, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button.jsx";
import { Input } from "@/components/ui/input.jsx";
import { Label } from "@/components/ui/label.jsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.jsx";
import { Alert, AlertDescription } from "@/components/ui/alert.jsx";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs.jsx";
import { useAuth } from "@/lib/context/AuthContext.jsx";
import { useToast } from "@/hooks/use-toast.js";
import routes from "@/routes/routes";
import authAPI, { applyServerErrors, mapAuthErrors } from "../lib/api/auth";
import { api } from "@/lib/api/api";

const loginSchema = z.object({
  email: z.string().email("Adresse e-mail invalide"),
  password: z
    .string()
    .min(6, "Le mot de passe doit contenir au moins 6 caractères"),
});

const signupSchema = z.object({
  first_name: z
    .string()
    .min(2, "Le prénom doit contenir au moins 2 caractères"),
  last_name: z
    .string()
    .min(2, "Le nom de famille doit contenir au moins 2 caractères"),
  email: z.string().email("Adresse e-mail invalide"),
  phone: z.string(),
  password: z.string(),
});

const AccountContext = createContext(null);

function AccountProvider({ children }) {
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("login");

  const navigate = useNavigate();
  const { login, signup, isAuthenticated, isLoading: authLoading } = useAuth();
  const { toast } = useToast();

  // Redirect authenticated users to main page immediately
  useEffect(() => {
    console.log("Auth state changed:", { isAuthenticated, authLoading });
    if (isAuthenticated && !authLoading) {
      console.log("Redirecting authenticated user to /societes");
      navigate("/societes", { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Additional immediate check for already authenticated users
  useEffect(() => {
    // Check if user is already authenticated on component mount
    const checkAuth = async () => {
      if (!authLoading) {
        if (isAuthenticated) {
          navigate("/societes", { replace: true });
        }
      }
    };
    checkAuth();
  }, [authLoading, isAuthenticated, navigate]);

  // Force redirect if user is authenticated (fallback)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isAuthenticated && !authLoading) {
        navigate("/societes", { replace: true });
      }
    }, 100); // Small delay to ensure state is properly set

    return () => clearTimeout(timer);
  }, [isAuthenticated, authLoading, navigate]);

  // Get invitation e-mail from search param key
  let [searchParams] = useSearchParams();
  const [invitationEmail, setInvitationEmail] = useState("");
  const [loadingInvitation, setLoadingInvitation] = useState(true);

  const checkInvitation = async () => {
    const searchParamsObj = Object.fromEntries(searchParams);
    const invitationKey = searchParamsObj["key"];

    if (!invitationKey) {
      setLoadingInvitation(false);
      return;
    }

    const response = await api.getInvitation(invitationKey);
    const invitation = response.ok ? await response.json() : null;

    if (!invitation) {
      toast({
        title: "Invitation expirée ou invalide",
        variant: "destructive"
      });
      setLoadingInvitation(false);
      return;
    }
    
    setActiveTab("signup");
    setInvitationEmail(invitation.email);
    toast({
        title: "Invitation",
        description: "Votre email a été pré-rempli",
      });
    setLoadingInvitation(false);
    
  };

  useEffect(() => {
    checkInvitation();
  }, []);

  const loginForm = useForm({
    resolver: zodResolver(loginSchema),
    mode: "onChange", // makes error disappear when user starts correcting input
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const signupForm = useForm({
    resolver: zodResolver(signupSchema),
    mode: "onChange", // makes error disappear when user starts correcting input
    values: {
      first_name: "",
      last_name: "",
      email: invitationEmail,
      phone: "",
      password: "",
    },
    resetOptions: {
      keepDirtyValues: true
    }
  });

  const onLoginSubmit = async (userCredentials) => {
    setIsLoading(true);

    const response = await login(userCredentials);

    if (!response.ok) {
      const jsonErrors = await response.json();
      const mapped = mapAuthErrors(jsonErrors.errors);
      applyServerErrors(loginForm.setError, mapped);
      setIsLoading(false);
      return;
    }

    toast({
      title: "Connexion réussie",
      description: "Vous êtes maintenant connecté à votre compte.",
    });
    navigate(routes.societes, { replace: true });
  };

  const onSignupSubmit = async (userData) => {
    setIsLoading(true);

    const response = await authAPI.signUp(userData);

    if (!response.ok) {
      const jsonErrors = await response.json();
      const mapped = mapAuthErrors(jsonErrors.errors);
      applyServerErrors(signupForm.setError, mapped);
      setIsLoading(false);
      return;
    }

    toast({
      title: "Compte créé avec succès",
      description: "Veuillez confirmer votre email pour activer votre compte.",
    });
    window.location.reload();
  };

  const clearMessages = () => {
    loginForm.clearErrors();
    signupForm.clearErrors();
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    clearMessages();
  };

  const value = {
    signupForm,
    loginForm,
    clearMessages,
    handleTabChange,
    onLoginSubmit,
    onSignupSubmit,
    isLoading,
    activeTab,
    invitationEmail,
    loadingInvitation
  };

  return (
    <AccountContext.Provider value={value}>{children}</AccountContext.Provider>
  );
}

function useAccount() {
  return useContext(AccountContext);
}

export default function Account() {
  return (
    <AccountProvider>
      <AccountComponent />
    </AccountProvider>
  );
}

function AccountComponent() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { handleTabChange, isLoading, activeTab, loadingInvitation} = useAccount();

  if (authLoading || loadingInvitation) {
    return (
      <AccountLoading>Vérification de l'authentification...</AccountLoading>
    );
  }

  if (isAuthenticated) {
    return <AccountLoading>Redirection vers l'application...</AccountLoading>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
            <span className="text-2xl font-bold text-primary">L</span>
          </div>
          <CardTitle className="text-2xl font-bold">Okiros Risk</CardTitle>
          <CardDescription>
            Gérez vos risques d'entreprise en toute simplicité
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login" className="flex items-center gap-2">
                <LogIn className="h-4 w-4" />
                Connexion
              </TabsTrigger>
              <TabsTrigger value="signup" className="flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                Inscription
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-4">
              <LoginForm />
            </TabsContent>

            <TabsContent value="signup" className="mt-4">
              <SignUpForm />
            </TabsContent>
          </Tabs>

          <div className="mt-6 text-center">
            <Button
              variant="link"
              className="text-sm text-muted-foreground hover:text-primary"
              onClick={() => navigate("/forgot-password")}
              disabled={isLoading}
            >
              Mot de passe oublié ?
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function LoginForm() {
  const { loginForm, onLoginSubmit, isLoading } = useAccount();
  const [showPassword, setShowPassword] = useState(false);
  return (
    <form
      onSubmit={loginForm.handleSubmit(onLoginSubmit)}
      className="space-y-4"
    >
      <div className="space-y-2">
        <Label htmlFor="login-email">Adresse e-mail</Label>
        <Input
          id="login-email"
          type="email"
          {...loginForm.register("email")}
          className={
            loginForm.formState.errors.email ? "border-destructive" : ""
          }
          disabled={isLoading}
        />
        {loginForm.formState.errors.email && (
          <p className="text-sm text-destructive">
            {loginForm.formState.errors.email.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="login-password">Mot de passe</Label>
        <div className="relative">
          <Input
            id="login-password"
            type={showPassword ? "text" : "password"}
            {...loginForm.register("password")}
            className={
              loginForm.formState.errors.password
                ? "border-destructive pr-10"
                : "pr-10"
            }
            disabled={isLoading}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
            onClick={() => setShowPassword(!showPassword)}
            disabled={isLoading}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </Button>
        </div>
        {loginForm.formState.errors.password && (
          <p className="text-sm text-destructive">
            {loginForm.formState.errors.password.message}
          </p>
        )}
      </div>

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Connexion en cours...
          </>
        ) : (
          "Se connecter"
        )}
      </Button>
    </form>
  );
}

function SignUpForm() {
  const { signupForm, onSignupSubmit, isLoading } = useAccount();
  const [showPassword, setShowPassword] = useState(false);

  return (
    <form
      onSubmit={signupForm.handleSubmit(onSignupSubmit)}
      className="space-y-4"
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="signup-first-name">Prénom</Label>
          <Input
            id="signup-first-name"
            type="text"
            {...signupForm.register("first_name")}
            className={
              signupForm.formState.errors.first_name ? "border-destructive" : ""
            }
            disabled={isLoading}
          />
          {signupForm.formState.errors.first_name && (
            <p className="text-sm text-destructive">
              {signupForm.formState.errors.first_name.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="signup-last-name">Nom de famille</Label>
          <Input
            id="signup-last-name"
            type="text"
            {...signupForm.register("last_name")}
            className={
              signupForm.formState.errors.last_name ? "border-destructive" : ""
            }
            disabled={isLoading}
          />
          {signupForm.formState.errors.last_name && (
            <p className="text-sm text-destructive">
              {signupForm.formState.errors.last_name.message}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="signup-email">Adresse e-mail</Label>
        <Input
          id="signup-email"
          type="email"
          {...signupForm.register("email")}
          className={
            signupForm.formState.errors.email ? "border-destructive" : ""
          }
          disabled={isLoading}
        />
        {signupForm.formState.errors.email && (
          <p className="text-sm text-destructive">
            {signupForm.formState.errors.email.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="signup-phone">Numéro de téléphone</Label>
        <Input
          id="signup-phone"
          type="text"
          pattern="[0-9]*"
          inputMode="numeric"
          {...signupForm.register("phone")}
          className={
            signupForm.formState.errors.phone ? "border-destructive" : ""
          }
          disabled={isLoading}
        />
        {signupForm.formState.errors.phone && (
          <p className="text-sm text-destructive">
            {signupForm.formState.errors.phone.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="signup-password">Mot de passe</Label>
        <div className="relative">
          <Input
            id="signup-password"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            {...signupForm.register("password")}
            className={
              signupForm.formState.errors.password
                ? "border-destructive pr-10"
                : "pr-10"
            }
            disabled={isLoading}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
            onClick={() => setShowPassword(!showPassword)}
            disabled={isLoading}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </Button>
        </div>
        {signupForm.formState.errors.password && (
          <p className="text-sm text-destructive">
            {signupForm.formState.errors.password.message}
          </p>
        )}
      </div>

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Création du compte...
          </>
        ) : (
          "Créer un compte"
        )}
      </Button>
    </form>
  );
}

function AccountLoading({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">{children}</p>
      </div>
    </div>
  );
}
